<script setup>
// ============================================================================
// BonBoZ: sơ đồ đường đi của thông báo — Hệ thống → Apple/Google → Thiết bị.
// Thay cho hai phễu cũ: nhìn một lượt là thấy tin rơi ở chặng nào, bao nhiêu.
//
// Luồng chỉ gồm lượt ĐÃ THẬT SỰ GỬI ĐI: mỗi ô ghi số lượt đi qua chặng đó, kèm
// % so với số đã gửi đi. Phần rơi ra ghi đúng chỗ nó rơi:
//   mũi tên 1  bị Apple/Google từ chối, đang gửi — đã gửi đi, chưa qua cửa họ
//   mũi tên 2  không tới máy — họ đã chuyển đi nhưng máy không báo nhận
//   ô 3        tới ngay / tới nhờ gửi lại
// Mọi thứ KHÔNG gửi đi (máy hỏng, tắt trong cài đặt, chưa có máy) gom vào một
// khung riêng dưới luồng, ghi rõ không tính vào số trong luồng: để không có
// dòng nào đứng cạnh một con số mà người đọc phải đoán có nằm trong đó không.
// Số cộng dồn khớp nhau, xem `pathTotals` trong `pushTimeline.js`. Cùng dữ
// liệu và cùng bộ lọc thiết bị với thanh 24 giờ.
//
// Màn hình hẹp: ba ô xếp dọc, mũi tên chỉ xuống, phần rơi ra nằm ngay cạnh
// mũi tên của chặng đó.
// ============================================================================
import { computed } from 'vue';
import SectionCard from './SectionCard.vue';
import { pathTotals } from './pushTimeline';
import { usePushReportFormat } from './pushReportFormat';

const props = defineProps({
  timeline: { type: Object, default: null },
  deviceScope: { type: String, default: 'phone' },
  loading: { type: Boolean, default: false },
});

const { formatNumber } = usePushReportFormat();

const totals = computed(() => pathTotals(props.timeline, props.deviceScope));

// So với số đã gửi đi: tỉ lệ thành công của đường truyền.
const percent = part =>
  totals.value.sent ? `${Math.round((part * 100) / totals.value.sent)}%` : '—';

const notSentAny = computed(
  () => totals.value.notSent || totals.value.off || totals.value.noDevice
);

const DEVICE_KEYS = {
  phone: 'BONBOZ.PUSH_REPORT.PATH.NODE_PHONE',
  desktop: 'BONBOZ.PUSH_REPORT.PATH.NODE_DESKTOP',
  all: 'BONBOZ.PUSH_REPORT.PATH.NODE_ALL',
};
</script>

<template>
  <SectionCard
    :title="$t('BONBOZ.PUSH_REPORT.PATH.TITLE')"
    :description="$t('BONBOZ.PUSH_REPORT.PATH.DESCRIPTION')"
  >
    <div v-if="loading" class="h-32 rounded-lg bg-n-alpha-2 animate-pulse" />
    <div
      v-else
      class="grid grid-cols-1 gap-y-2 md:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,1fr)] md:gap-x-3"
      data-testid="push-path"
    >
      <!-- Ô 1: hệ thống của mình -->
      <div class="flex flex-col min-w-0 gap-2">
        <div
          class="flex flex-col gap-0.5 p-3 border rounded-lg border-n-weak bg-n-alpha-1"
          data-testid="push-path-system"
        >
          <span class="text-xs text-n-slate-11">
            {{ $t('BONBOZ.PUSH_REPORT.PATH.NODE_SYSTEM') }}
          </span>
          <span class="text-xl font-semibold tabular-nums text-n-slate-12">
            {{ formatNumber(totals.sent) }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ $t('BONBOZ.PUSH_REPORT.PATH.SENT_UNIT') }}
          </span>
        </div>
      </div>

      <!-- Mũi tên 1: hệ thống → Apple/Google. Rơi: bị từ chối, đang gửi. -->
      <div
        class="flex items-start min-w-0 gap-2 ps-4 md:flex-col md:items-center md:ps-0 md:pt-7"
      >
        <span
          class="size-5 shrink-0 text-n-slate-9 i-lucide-arrow-down md:hidden"
          aria-hidden="true"
        />
        <span
          class="hidden size-5 text-n-slate-9 i-lucide-arrow-right md:block"
          aria-hidden="true"
        />
        <p
          v-if="totals.rejected || totals.pending"
          class="flex flex-col gap-0.5 m-0 text-xs leading-snug md:text-center"
        >
          <span
            v-if="totals.rejected"
            class="text-n-ruby-11"
            data-testid="push-path-rejected"
          >
            {{
              $t('BONBOZ.PUSH_REPORT.PATH.REJECTED', {
                count: formatNumber(totals.rejected),
              })
            }}
          </span>
          <span
            v-if="totals.pending"
            class="text-n-slate-11"
            data-testid="push-path-pending"
          >
            {{
              $t('BONBOZ.PUSH_REPORT.PATH.PENDING', {
                count: formatNumber(totals.pending),
              })
            }}
          </span>
        </p>
      </div>

      <!-- Ô 2: Apple / Google -->
      <div class="flex flex-col min-w-0 gap-2">
        <div
          class="flex flex-col gap-0.5 p-3 border rounded-lg border-n-weak bg-n-alpha-1"
          data-testid="push-path-provider"
        >
          <span class="text-xs text-n-slate-11">
            {{ $t('BONBOZ.PUSH_REPORT.PATH.NODE_PROVIDER') }}
          </span>
          <span class="text-xl font-semibold tabular-nums text-n-slate-12">
            {{ formatNumber(totals.accepted) }}
            <span class="text-sm font-normal text-n-slate-11">
              {{ percent(totals.accepted) }}
            </span>
          </span>
          <span class="text-xs text-n-slate-11">
            {{ $t('BONBOZ.PUSH_REPORT.PATH.ACCEPTED_UNIT') }}
          </span>
        </div>
      </div>

      <!-- Mũi tên 2: Apple/Google → thiết bị. Rơi: không tới máy. -->
      <div
        class="flex items-start min-w-0 gap-2 ps-4 md:flex-col md:items-center md:ps-0 md:pt-7"
      >
        <span
          class="size-5 shrink-0 text-n-slate-9 i-lucide-arrow-down md:hidden"
          aria-hidden="true"
        />
        <span
          class="hidden size-5 text-n-slate-9 i-lucide-arrow-right md:block"
          aria-hidden="true"
        />
        <p
          v-if="totals.noAck"
          class="m-0 text-xs leading-snug md:text-center text-n-ruby-11"
          data-testid="push-path-no-ack"
        >
          {{
            $t('BONBOZ.PUSH_REPORT.PATH.NO_ACK', {
              count: formatNumber(totals.noAck),
            })
          }}
        </p>
      </div>

      <!-- Ô 3: thiết bị -->
      <div class="flex flex-col min-w-0 gap-2">
        <div
          class="flex flex-col gap-0.5 p-3 border rounded-lg border-n-teal-7 bg-n-teal-2"
          data-testid="push-path-device"
        >
          <span class="text-xs text-n-slate-11">
            {{ $t(DEVICE_KEYS[deviceScope] || DEVICE_KEYS.phone) }}
          </span>
          <span class="text-xl font-semibold tabular-nums text-n-slate-12">
            {{ formatNumber(totals.reached) }}
            <span class="text-sm font-normal text-n-slate-11">
              {{ percent(totals.reached) }}
            </span>
          </span>
          <span class="text-xs text-n-slate-11">
            {{ $t('BONBOZ.PUSH_REPORT.PATH.REACHED_UNIT') }}
          </span>
        </div>
        <p class="flex flex-col gap-0.5 m-0 text-xs leading-snug">
          <span class="text-n-slate-12" data-testid="push-path-ok">
            {{
              $t('BONBOZ.PUSH_REPORT.PATH.OK', {
                count: formatNumber(totals.ok),
              })
            }}
          </span>
          <span
            v-if="totals.resent"
            class="text-n-amber-11"
            data-testid="push-path-resent"
          >
            {{
              $t('BONBOZ.PUSH_REPORT.PATH.RESENT', {
                count: formatNumber(totals.resent),
              })
            }}
          </span>
        </p>
      </div>
    </div>

    <!-- Không gửi đi: tách khỏi luồng, không tính vào số nào ở trên. -->
    <div
      v-if="!loading && notSentAny"
      class="flex flex-col gap-1 p-3 mt-4 text-xs border border-dashed rounded-lg border-n-weak"
      data-testid="push-path-not-sent-box"
    >
      <span class="font-medium text-n-slate-12">
        {{ $t('BONBOZ.PUSH_REPORT.PATH.NOT_SENT_TITLE') }}
      </span>
      <span
        v-if="totals.notSent"
        class="text-n-ruby-11"
        data-testid="push-path-not-sent"
      >
        {{
          $t('BONBOZ.PUSH_REPORT.PATH.NOT_SENT', {
            count: formatNumber(totals.notSent),
          })
        }}
      </span>
      <span
        v-if="totals.off"
        class="text-n-slate-11"
        data-testid="push-path-off"
      >
        {{
          $t('BONBOZ.PUSH_REPORT.PATH.OFF', { count: formatNumber(totals.off) })
        }}
      </span>
      <span
        v-if="totals.noDevice"
        class="text-n-slate-11"
        data-testid="push-path-no-device"
      >
        {{
          $t('BONBOZ.PUSH_REPORT.PATH.NO_DEVICE', {
            count: formatNumber(totals.noDevice),
          })
        }}
      </span>
    </div>
  </SectionCard>
</template>
